import { useState } from 'react';
import { Task, User, Priority } from '../types';
import { Target, TrendingUp, Clock, Users, AlertCircle, CheckCircle2, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { useNavigate } from 'react-router';
import { ProgressStatCard } from './StatCards';
import { getPriorityColors, getStatusColors } from '../constants/colorScheme';

interface MilestoneTrackerProps {
  tasks: Task[];
  users: User[];
  canChangeMilestone?: boolean;
  onMilestoneChange?: (milestone: Priority) => void;
  activeMilestone?: Priority;
}

export default function MilestoneTracker({
  tasks,
  users,
  canChangeMilestone = false,
  onMilestoneChange,
  activeMilestone = 'P0',
}: MilestoneTrackerProps) {
  const [selectedMilestone, setSelectedMilestone] = useState<Priority>(activeMilestone);
  const navigate = useNavigate();

  // Filter tasks by selected milestone
  const milestoneTasks = tasks.filter((t) => t.priority === selectedMilestone);

  // Calculate metrics
  const totalTasks = milestoneTasks.length;
  const completedTasks = milestoneTasks.filter(
    (t) => t.status === 'Done' || t.status === 'Completed'
  ).length;
  const inProgressTasks = milestoneTasks.filter(
    (t) => t.status === 'In Progress' || t.status === 'Active'
  ).length;
  const blockedTasks = milestoneTasks.filter((t) => t.status === 'Blocked').length;
  const notStartedTasks = milestoneTasks.filter((t) => t.status === 'Not Started').length;

  const completionPercent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  // Time metrics
  const totalEstimated = milestoneTasks.reduce((sum, t) => sum + (t.estimatedHours || 0), 0);
  const totalLogged = milestoneTasks.reduce(
    (sum, t) => sum + (t.timeLogs?.reduce((s, log) => s + log.hours, 0) || 0),
    0
  );
  const remainingHours = milestoneTasks
    .filter((t) => t.status !== 'Done' && t.status !== 'Completed')
    .reduce((sum, t) => {
      const logged = t.timeLogs?.reduce((s, log) => s + log.hours, 0) || 0;
      return sum + Math.max(0, (t.estimatedHours || 0) - logged);
    }, 0);

  // Calculate velocity (average hours per day from recent time logs)
  const recentLogs = milestoneTasks
    .flatMap((t) => t.timeLogs || [])
    .filter((log) => {
      const logDate = new Date(log.date);
      const daysAgo = (Date.now() - logDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo <= 7; // Last 7 days
    });

  const velocityHoursPerDay = recentLogs.length > 0 ? recentLogs.reduce((sum, log) => sum + log.hours, 0) / 7 : 0;

  // Project completion date
  const daysToComplete = velocityHoursPerDay > 0 ? remainingHours / velocityHoursPerDay : 0;
  const projectedDate = new Date();
  projectedDate.setDate(projectedDate.getDate() + Math.ceil(daysToComplete));

  // Get unique assignees working on milestone
  const assigneeIds = new Set(milestoneTasks.flatMap((t) => t.assigneeIds));
  const activeAssignees = users.filter((u) => assigneeIds.has(u.id));

  // Find critical blockers
  const criticalBlockers = milestoneTasks.filter(
    (t) => t.status === 'Blocked' || (t.dependencies && t.dependencies.split(',').length > 0)
  );

  const handleMilestoneChange = (newMilestone: Priority) => {
    setSelectedMilestone(newMilestone);
    if (onMilestoneChange) {
      onMilestoneChange(newMilestone);
    }
  };

  const milestoneOptions: Priority[] = ['P0', 'P1', 'P2', 'Low', 'Medium', 'High', 'Critical'];
  const milestoneBadgeClass = getPriorityColors(selectedMilestone).badge;
  const activeStatus = getStatusColors('Active');
  const completedStatus = getStatusColors('Completed');
  const blockedStatus = getStatusColors('Blocked');
  const deferredStatus = getStatusColors('Deferred');

  return (
    <Card className="border-border shadow-lg">
      <CardHeader className="bg-muted/40">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" />
            <CardTitle className="text-xl">Active Milestone</CardTitle>
          </div>
          {canChangeMilestone ? (
            <Select value={selectedMilestone} onValueChange={handleMilestoneChange}>
              <SelectTrigger className="h-9 w-32 bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {milestoneOptions.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className={`px-4 py-2 text-lg ${milestoneBadgeClass}`}>{selectedMilestone}</Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-6">
        {totalTasks === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Target className="mx-auto mb-3 h-16 w-16 opacity-20" />
            <p className="text-lg font-medium">No tasks in {selectedMilestone}</p>
            <p className="text-sm mt-1">Select a different milestone or create tasks</p>
          </div>
        ) : (
          <>
            {/* Overall Progress */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">
                  Overall Progress
                </h3>
                <span className="text-2xl font-bold text-primary">
                  {completionPercent.toFixed(0)}%
                </span>
              </div>
              <Progress value={completionPercent} className="h-4" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <ProgressStatCard
                  label="Completed"
                  value={completedTasks}
                  total={totalTasks}
                  color="green"
                  icon={<CheckCircle2 className="w-4 h-4" />}
                />
                <ProgressStatCard
                  label="In Progress"
                  value={inProgressTasks}
                  total={totalTasks}
                  color="blue"
                  icon={<TrendingUp className="w-4 h-4" />}
                />
                <ProgressStatCard
                  label="Not Started"
                  value={notStartedTasks}
                  total={totalTasks}
                  color="gray"
                  icon={<Clock className="w-4 h-4" />}
                />
                <ProgressStatCard
                  label="Blocked"
                  value={blockedTasks}
                  total={totalTasks}
                  color="red"
                  icon={<AlertCircle className="w-4 h-4" />}
                />
              </div>
            </div>

            {/* Time Tracking */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border border-border bg-muted/40 p-4">
                <div className="mb-1 text-sm text-muted-foreground">Estimated</div>
                <div className="text-2xl font-bold text-foreground">
                  {totalEstimated.toFixed(0)}h
                </div>
              </div>
              <div className={`rounded-lg p-4 ${completedStatus.container}`}>
                <div className={`mb-1 text-sm ${completedStatus.text}`}>Logged</div>
                <div className="text-2xl font-bold text-foreground">
                  {totalLogged.toFixed(0)}h
                </div>
                <div className={`text-xs ${completedStatus.text}`}>
                  {totalEstimated > 0 ? ((totalLogged / totalEstimated) * 100).toFixed(0) : 0}% of estimate
                </div>
              </div>
              <div className={`rounded-lg p-4 ${deferredStatus.container}`}>
                <div className={`mb-1 text-sm ${deferredStatus.text}`}>Remaining</div>
                <div className="text-2xl font-bold text-foreground">
                  {remainingHours.toFixed(0)}h
                </div>
              </div>
            </div>

            {/* Projected Completion */}
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  <div>
                    <div className="mb-1 text-sm text-muted-foreground">
                      Projected Completion
                    </div>
                    <div className="text-xl font-bold text-foreground">
                      {velocityHoursPerDay > 0 ? (
                        projectedDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      ) : (
                        <span className="text-base text-muted-foreground">Not enough data</span>
                      )}
                    </div>
                  </div>
                </div>
                {velocityHoursPerDay > 0 && (
                  <div className="text-right">
                    <div className="text-sm text-foreground">
                      ~{Math.ceil(daysToComplete)} days
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {velocityHoursPerDay.toFixed(1)}h/day velocity
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Team & Warnings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Active Team */}
              <div className="rounded-lg border border-border bg-muted/40 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-semibold text-foreground">Active Team</h4>
                </div>
                <div className="space-y-2">
                  {activeAssignees.slice(0, 5).map((user) => {
                    const userTasks = milestoneTasks.filter((t) => t.assigneeIds.includes(user.id));
                    const userCompleted = userTasks.filter(
                      (t) => t.status === 'Done' || t.status === 'Completed'
                    ).length;
                    return (
                      <div key={user.id} className="flex items-center justify-between text-sm">
                        <span className="flex-1 truncate font-medium text-foreground">
                          {user.name}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {userCompleted}/{userTasks.length}
                        </Badge>
                      </div>
                    );
                  })}
                  {activeAssignees.length > 5 && (
                    <div className="text-xs text-muted-foreground">
                      +{activeAssignees.length - 5} more
                    </div>
                  )}
                  {activeAssignees.length === 0 && (
                    <div className="text-sm italic text-muted-foreground">No assignees</div>
                  )}
                </div>
              </div>

              {/* Warnings */}
              <div className={`rounded-lg p-4 ${deferredStatus.container}`}>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className={`h-4 w-4 ${deferredStatus.text}`} />
                  <h4 className={`font-semibold ${deferredStatus.text}`}>Attention Needed</h4>
                </div>
                <div className="space-y-2 text-sm">
                  {blockedTasks > 0 && (
                    <div className="flex items-center justify-between">
                      <span className={deferredStatus.text}>Blocked tasks</span>
                      <Badge variant="outline" className={`text-xs ${blockedStatus.badge}`}>
                        {blockedTasks}
                      </Badge>
                    </div>
                  )}
                  {criticalBlockers.length > 0 && (
                    <div className="flex items-center justify-between">
                      <span className={deferredStatus.text}>Has dependencies</span>
                      <Badge variant="outline" className={`text-xs ${deferredStatus.badge}`}>
                        {criticalBlockers.length}
                      </Badge>
                    </div>
                  )}
                  {totalLogged > totalEstimated * 1.2 && (
                    <div className="flex items-center justify-between">
                      <span className={deferredStatus.text}>Over estimate</span>
                      <Badge variant="outline" className={`text-xs ${blockedStatus.badge}`}>
                        {((totalLogged / totalEstimated) * 100 - 100).toFixed(0)}%
                      </Badge>
                    </div>
                  )}
                  {blockedTasks === 0 && criticalBlockers.length === 0 && totalLogged <= totalEstimated * 1.2 && (
                    <div className={`italic ${completedStatus.text}`}>
                      ✓ No issues detected
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 border-t border-border pt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/dashboard?filter=${selectedMilestone}`)}
                className="flex-1"
              >
                View All Tasks
              </Button>
              <Button
                size="sm"
                onClick={() => navigate(`/milestone/${selectedMilestone}`)}
                className="flex-1"
              >
                Detailed View
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
